import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneTilt, Wrench, ChatCircleText, CreditCard, ArrowLeft, Shield, Key, Eye, EyeSlash } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function centsToUsd(c) { return `$${((c || 0) / 100).toFixed(2)}`; }

function PinScreen({ onDone, token }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN must be 4 digits"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/public/chew/portal/${token}/pin/verify`, { pin });
      onDone(pin, { student_first_name: data.student_first_name || "" });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Wrong PIN")); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#f59e0b] p-5" data-testid="pin-screen">
        <div className="flex items-center gap-2 mb-3">
          <Shield weight="fill" size={28} className="text-amber-500"/>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-500">Chew Flats</div>
            <div className="font-display font-black text-2xl leading-tight">Enter your PIN</div>
          </div>
        </div>
        <div className="text-xs text-slate-600 mb-4">
          Enter the 4-digit PIN you created on first visit.
        </div>
        <input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g,'').slice(0,4))}
          data-testid="pin-input" inputMode="numeric" placeholder="••••" autoFocus
          className="w-full h-14 text-center text-4xl font-black tracking-[0.5em] border-2 border-[#0f172a]"/>
        <button onClick={submit} disabled={busy || pin.length !== 4} data-testid="pin-submit"
          className="mt-4 w-full h-12 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
          {busy ? "Checking…" : "Unlock"}
        </button>
        <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">
          Powered by Field Radar · LaSalle Housing
        </div>
      </div>
    </div>
  );
}

function IdentityScreen({ token, unitLabel, brand, textOnBrand, knownResidents, onDone }) {
  const [firstName, setFirstName] = useState("");
  const [notListed, setNotListed] = useState(false);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const hasRoster = (knownResidents || []).length > 0;
  const canSubmit = firstName.trim().length >= 1
    && /^\d{4}$/.test(pin)
    && pin === confirmPin;
  const submit = async () => {
    if (!canSubmit) {
      if (pin !== confirmPin) { toast.error("PINs don't match"); return; }
      toast.error("Enter your name and a 4-digit PIN"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/chew/portal/${token}/identity/set`, {
        first_name: firstName.trim(),
        pin,
      });
      toast.success(`Welcome, ${firstName.trim()}!`);
      onDone(pin, { student_first_name: firstName.trim() });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't save")); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#f59e0b] p-5" data-testid="identity-screen">
        <div className="flex items-center gap-2 mb-1">
          <Shield weight="fill" size={28} style={{ color: brand }}/>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: brand }}>Chew Flats · {unitLabel}</div>
            <div className="font-display font-black text-2xl leading-tight">Welcome 👋</div>
          </div>
        </div>
        <div className="text-xs text-slate-600 mb-4 mt-1">
          Set up your resident portal — takes 15 seconds. LaSalle staff only see your first name + unit.
        </div>

        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          First name {hasRoster && !notListed && <span className="text-slate-400 normal-case tracking-normal">· from your door sticker</span>}
        </label>
        {hasRoster && !notListed ? (
          <>
            <input value={firstName} onChange={e => setFirstName(e.target.value)}
              data-testid="identity-first-name" placeholder="Start typing…" autoFocus
              list="roster-datalist" maxLength={40}
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            <datalist id="roster-datalist">
              {knownResidents.map((n, i) => <option key={i} value={n} />)}
            </datalist>
            <button type="button" onClick={() => { setNotListed(true); setFirstName(""); }}
              data-testid="identity-not-listed"
              className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 underline">
              Not on the sticker? Type yours →
            </button>
          </>
        ) : (
          <>
            <input value={firstName} onChange={e => setFirstName(e.target.value)}
              data-testid="identity-first-name" placeholder="Sarah" autoFocus maxLength={40}
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            {hasRoster && (
              <button type="button" onClick={() => setNotListed(false)}
                className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 underline">
                ← Back to sticker list
              </button>
            )}
          </>
        )}

        <div className="mt-4 pt-3 border-t-2 border-slate-100">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Pick a 4-digit PIN</label>
          <input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g,'').slice(0,4))}
            data-testid="identity-pin" inputMode="numeric" placeholder="••••"
            className="mt-1 w-full h-12 text-center text-3xl font-black tracking-[0.5em] border-2 border-[#0f172a]"/>
          <input value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g,'').slice(0,4))}
            data-testid="identity-pin-confirm" inputMode="numeric" placeholder="confirm"
            className="mt-2 w-full h-10 text-center text-xl font-black tracking-[0.5em] border-2 border-slate-300"/>
        </div>

        <button onClick={submit} disabled={busy || !canSubmit} data-testid="identity-submit"
          style={{ background: canSubmit ? brand : "#94a3b8", color: canSubmit ? textOnBrand : "#fff" }}
          className="mt-4 w-full h-12 font-black uppercase text-xs tracking-widest disabled:opacity-60">
          {busy ? "Saving…" : "Save & Continue →"}
        </button>
        <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">
          Powered by Field Radar · LaSalle Housing
        </div>
      </div>
    </div>
  );
}

function HomeTiles({ info, onGo }) {
  return (
    <div className="grid grid-cols-1 gap-3">
      <button onClick={() => onGo("triage")} data-testid="tile-triage"
        className="text-left p-4 bg-white border-2 border-[#0f172a] hover:shadow-[6px_6px_0px_#f59e0b] transition-shadow flex items-center gap-3">
        <ChatCircleText weight="fill" size={40} className="text-amber-500 shrink-0"/>
        <div>
          <div className="font-display font-black text-xl uppercase tracking-tight">AI Helper</div>
          <div className="text-xs text-slate-600">Something broken? Talk to our AI first — most fixes take 60 seconds.</div>
        </div>
      </button>
      <button onClick={() => onGo("ticket")} data-testid="tile-ticket"
        className="text-left p-4 bg-white border-2 border-[#0f172a] hover:shadow-[6px_6px_0px_#3b82f6] transition-shadow flex items-center gap-3">
        <Wrench weight="fill" size={40} className="text-blue-500 shrink-0"/>
        <div>
          <div className="font-display font-black text-xl uppercase tracking-tight">Submit a Ticket</div>
          <div className="text-xs text-slate-600">Sends to LaSalle facilities + PMR maintenance. CC's Leslie if it needs approval.</div>
        </div>
      </button>
      {info.has_codes && (
        <button onClick={() => onGo("codes")} data-testid="tile-codes"
          className="text-left p-4 bg-white border-2 border-[#0f172a] hover:shadow-[6px_6px_0px_#ef4444] transition-shadow flex items-center gap-3">
          <Key weight="fill" size={40} className="text-red-500 shrink-0"/>
          <div>
            <div className="font-display font-black text-xl uppercase tracking-tight">Door Codes</div>
            <div className="text-xs text-slate-600">Push-button + key ID — tap to reveal, auto-hides in 30s. Every peek is logged.</div>
          </div>
        </button>
      )}
      <button onClick={() => onGo("laundry")} data-testid="tile-laundry"
        className="text-left p-4 bg-white border-2 border-[#0f172a] hover:shadow-[6px_6px_0px_#10b981] transition-shadow flex items-center gap-3">
        <CreditCard weight="fill" size={40} className="text-emerald-500 shrink-0"/>
        <div>
          <div className="font-display font-black text-xl uppercase tracking-tight">Laundry Balance</div>
          <div className="text-xs text-slate-600">Track your card usage — covered by LaSalle.</div>
        </div>
      </button>
    </div>
  );
}

function TriageChat({ token, pin, preview, onEscalate }) {
  const [messages, setMessages] = useState([
    { role: "ai", text: "Hey! I'm the Chew Flats maintenance helper. Tell me what's going on and I'll try to sort it out — or route you to LaSalle facilities if it needs a tech." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [chipsUsed, setChipsUsed] = useState(0);   // reset per AI turn so chips reappear
  const sessionId = useRef("chew-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8));
  const scrollRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo(0, 99999); }, [messages]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text) return;
    setMessages(m => [...m, { role: "student", text }]);
    setInput("");
    setBusy(true);
    setChipsUsed(c => c + 1);   // hide chips once used until next AI reply
    try {
      const { data } = await axios.post(
        `${BASE}/api/public/chew/portal/${token}/triage${preview ? "?preview=1" : ""}`,
        { pin, session_id: sessionId.current, message: text },
      );
      setMessages(m => [...m, { role: "ai", text: data.response || "…" }]);
      setChipsUsed(0);   // fresh AI reply — offer chips again
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI hiccup — try again")); }
    finally { setBusy(false); }
  };

  // Chips render on the LAST message if it's AI + ends with a question mark.
  // We check the last non-empty line so "…" at the end of the response
  // doesn't defeat the detection.
  const last = messages[messages.length - 1];
  const showChips = !busy && chipsUsed === 0 && last?.role === "ai"
                    && /\?\s*$/.test((last.text || "").split("\n").filter(Boolean).slice(-1)[0] || "");

  return (
    <div className="flex flex-col h-[60vh] bg-white border-2 border-[#0f172a]" data-testid="triage-chat">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "student" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] p-2.5 text-sm border-2 whitespace-pre-wrap ${m.role === "student" ? "bg-amber-100 border-amber-500" : "bg-white border-[#0f172a]"}`}>{m.text}</div>
          </div>
        ))}
        {busy && <div className="text-xs italic text-slate-400">AI is thinking…</div>}
        {showChips && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="triage-chips">
            <button onClick={() => send("Yes")} data-testid="triage-chip-yes"
              className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest">
              ✓ Yes
            </button>
            <button onClick={() => send("No, still not working")} data-testid="triage-chip-no"
              className="h-9 px-3 bg-red-500 hover:bg-red-600 text-white font-black uppercase text-[10px] tracking-widest">
              ✗ No
            </button>
            <button onClick={() => setChipsUsed(1)} data-testid="triage-chip-other"
              className="h-9 px-3 bg-white border-2 border-slate-300 hover:bg-slate-100 text-slate-600 font-black uppercase text-[10px] tracking-widest">
              💬 Something else
            </button>
          </div>
        )}
      </div>
      <div className="p-2 border-t-2 border-slate-200 flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          data-testid="triage-input"
          placeholder="e.g. My kitchen sink is dripping"
          className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm"/>
        <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="triage-send"
          className="h-11 px-4 bg-[#0f172a] hover:bg-slate-800 text-white disabled:opacity-40">
          <PaperPlaneTilt weight="fill" size={18}/>
        </button>
      </div>
      <button onClick={() => onEscalate(sessionId.current)} data-testid="triage-escalate"
        className="p-3 bg-blue-500 hover:bg-blue-600 text-white font-black uppercase text-[10px] tracking-widest">
        → Open a Ticket Instead
      </button>
    </div>
  );
}

function TicketForm({ token, pin, preview, info, initialSession, onSubmitted }) {
  const [form, setForm] = useState({
    subject: "", description: "", category: "other",
    estimated_cost_cents: 0, triage_session_id: initialSession || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.subject.trim() || !form.description.trim()) { toast.error("Subject + description required"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/public/chew/portal/${token}/ticket${preview ? "?preview=1" : ""}`,
        { ...form, pin },
      );
      toast.success(data.needs_approval
        ? `Ticket sent to Facilities + Leslie (approval needed)`
        : `Ticket sent to LaSalle Facilities + PMR`, { duration: 6000 });
      onSubmitted();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't submit")); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border-2 border-[#0f172a] p-3 space-y-3" data-testid="ticket-form">
      <input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
        data-testid="ticket-subject" placeholder="What's the issue? (short)"
        className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
      <textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
        data-testid="ticket-description" placeholder="Describe it in detail. When did it start? What did you already try?"
        className="w-full px-3 py-2 border-2 border-[#0f172a] text-sm resize-none"/>
      <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
        data-testid="ticket-category" className="w-full h-11 px-2 border-2 border-slate-300 text-sm bg-white">
        <option value="plumbing">Plumbing</option>
        <option value="electrical">Electrical</option>
        <option value="hvac">HVAC / Heat / A-C</option>
        <option value="appliance">Appliance (fridge, stove, disposal)</option>
        <option value="fixtures">Fixtures (locks, blinds, hinges)</option>
        <option value="cleaning">Cleaning</option>
        <option value="safety">Safety (fire alarm, GFCI, mold)</option>
        <option value="other">Other</option>
      </select>
      <button onClick={submit} disabled={busy} data-testid="ticket-submit"
        className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
        {busy ? "Sending…" : "Send to LaSalle Facilities"}
      </button>
    </div>
  );
}


function CodeCard({ label, note, value, revealed, countdown, tone }) {
  const toneClasses = {
    door: "shadow-[3px_3px_0_#ef4444] border-red-500",
    key: "shadow-[3px_3px_0_#0f172a] border-[#0f172a]",
    laundry: "shadow-[3px_3px_0_#10b981] border-emerald-500",
  }[tone] || "shadow-[3px_3px_0_#0f172a] border-[#0f172a]";
  return (
    <div className={`bg-white border-2 p-3 mb-3 ${toneClasses}`} data-testid={`code-card-${tone}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
        {revealed && countdown > 0 && (
          <div className="text-[10px] font-black uppercase tracking-widest text-red-500"
               data-testid={`code-countdown-${tone}`}>
            HIDES IN {countdown}s
          </div>
        )}
      </div>
      {revealed ? (
        <div className="mt-2 font-mono font-black text-3xl tracking-widest text-center py-2 bg-[#0f172a] text-amber-300"
             data-testid={`code-value-${tone}`}>
          {value || "—"}
        </div>
      ) : (
        <div className="mt-2 text-center py-3 text-slate-400 font-mono font-black text-2xl tracking-widest select-none">
          ••••••
        </div>
      )}
      {note && <div className="mt-2 text-[11px] text-slate-500 italic">{note}</div>}
    </div>
  );
}

function DoorCodesPanel({ token, unitLabel }) {
  const [summary, setSummary] = useState(null);
  const [revealed, setRevealed] = useState(null); // {door_code, key_code, laundry_code, key_qty}
  const [pinInput, setPinInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    axios.get(`${BASE}/api/public/chew/portal/${token}/codes`)
      .then(r => setSummary(r.data))
      .catch(() => toast.error("Couldn't load codes"));
  }, [token]);

  // 30-second countdown that clears the revealed values automatically.
  useEffect(() => {
    if (!revealed) return;
    if (countdown <= 0) { setRevealed(null); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, revealed]);

  const reveal = async () => {
    if (!/^\d{4}$/.test(pinInput)) { toast.error("Enter your 4-digit PIN"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/public/chew/portal/${token}/codes/reveal`,
        { pin: pinInput });
      setRevealed(data.codes);
      setCountdown(data.expires_in_seconds || 30);
      setPinInput("");
      toast.success("Revealed — auto-hides in 30s");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Wrong PIN"));
      setPinInput("");
    } finally { setBusy(false); }
  };

  const hideNow = () => { setRevealed(null); setCountdown(0); };

  if (!summary) return <div className="p-6 text-xs font-black uppercase text-slate-400">Loading…</div>;
  if (summary.count === 0) {
    return (
      <div className="p-4 bg-white border-2 border-[#0f172a] text-xs text-slate-600" data-testid="codes-empty">
        No codes on file for this unit yet — contact LaSalle housing.
      </div>
    );
  }

  const doorMeta = summary.codes.find(c => c.kind === "door");
  const keyMeta = summary.codes.find(c => c.kind === "key");
  const laundryMeta = summary.codes.find(c => c.kind === "laundry");

  return (
    <div className="space-y-3" data-testid="door-codes-panel">
      <div className="p-3 bg-red-50 border-2 border-red-500 text-[11px] text-red-800 leading-snug">
        <b className="uppercase tracking-widest text-[10px]">Private</b> — every reveal is logged with your unit + time. Never share these with anyone outside your unit.
      </div>

      {doorMeta && (
        <CodeCard tone="door" label={doorMeta.label}
          value={revealed?.door_code}
          revealed={!!revealed?.door_code}
          countdown={countdown}/>
      )}
      {keyMeta && (
        <CodeCard tone="key"
          label={`${keyMeta.label}${keyMeta.qty ? ` · ${keyMeta.qty} issued` : ""}`}
          note={revealed ? "Tell LaSalle this ID if you lose your key — they order a re-cut." : ""}
          value={revealed?.key_code}
          revealed={!!revealed?.key_code}
          countdown={countdown}/>
      )}
      {laundryMeta && (
        <CodeCard tone="laundry" label={laundryMeta.label}
          value={revealed?.laundry_code}
          revealed={!!revealed?.laundry_code}
          countdown={countdown}/>
      )}

      {summary.keys_location_note && (
        <div className="text-[11px] text-slate-500 italic text-center px-3">
          {summary.keys_location_note}
        </div>
      )}
      {summary.unit_code_note && (
        <div className="p-2 bg-amber-100 border-2 border-amber-500 text-[11px] font-black uppercase tracking-widest text-amber-800">
          ⚠️ {summary.unit_code_note}
        </div>
      )}

      {revealed ? (
        <button onClick={hideNow} data-testid="codes-hide-now"
          className="w-full h-11 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest flex items-center justify-center gap-2">
          <EyeSlash weight="fill" size={16}/> Hide now
        </button>
      ) : (
        <div className="pt-2 border-t-2 border-slate-100">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Confirm PIN to reveal</label>
          <div className="flex gap-2 mt-1">
            <input value={pinInput}
              onChange={e => setPinInput(e.target.value.replace(/\D/g,'').slice(0,4))}
              onKeyDown={e => e.key === "Enter" && reveal()}
              data-testid="codes-pin-input"
              inputMode="numeric" placeholder="••••"
              className="flex-1 h-11 text-center text-2xl font-black tracking-[0.5em] border-2 border-[#0f172a]"/>
            <button onClick={reveal} disabled={busy || pinInput.length !== 4}
              data-testid="codes-reveal-btn"
              className="h-11 px-4 bg-red-500 hover:bg-red-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40 flex items-center gap-1">
              <Eye weight="fill" size={14}/> {busy ? "…" : "Reveal"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LaundryPanel({ token, pin, preview }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams();
    if (preview) params.set("preview", "1");
    if (pin) params.set("pin", pin);
    axios.get(`${BASE}/api/public/chew/portal/${token}/laundry?${params.toString()}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Couldn't load laundry"));
  }, [token, pin, preview]);
  if (!data) return <div className="p-6 text-xs font-black uppercase text-slate-400">Loading…</div>;
  const cap = data.soft_cap_cents;
  const pct = cap > 0 ? Math.min(100, Math.round((data.balance_cents / cap) * 100)) : 0;
  const overCap = data.balance_cents > cap;
  return (
    <div className="space-y-3" data-testid="laundry-panel">
      <div className="p-4 bg-white border-2 border-[#0f172a]">
        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600">This month · covered by {data.covered_by}</div>
        <div className="font-display font-black text-4xl mt-1" data-testid="laundry-balance">{centsToUsd(data.balance_cents)}</div>
        <div className="text-xs text-slate-500">Soft cap: {centsToUsd(cap)} · you won't be charged, just a heads-up</div>
        <div className="mt-3 h-3 bg-slate-100 border-2 border-[#0f172a]">
          <div className={`h-full ${overCap ? "bg-red-500" : pct > 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }}/>
        </div>
        {overCap && (
          <div className="mt-2 p-2 bg-amber-50 border-2 border-amber-500 text-[11px] font-black uppercase tracking-widest text-amber-800" data-testid="laundry-cap-warning">
            ⚠️ Over the monthly soft cap — heads-up, LaSalle still covers it
          </div>
        )}
      </div>
      <div className="bg-white border-2 border-[#0f172a]">
        <div className="p-2 border-b-2 border-slate-200 font-black uppercase tracking-widest text-[10px]">Recent charges</div>
        <div className="divide-y divide-slate-200">
          {(data.ledger || []).length === 0 && <div className="p-4 text-xs text-slate-400 text-center">No charges yet.</div>}
          {(data.ledger || []).slice().reverse().map((r, i) => (
            <div key={i} className="p-2 flex items-center justify-between text-xs">
              <div>
                <div className="font-black">{r.description}</div>
                <div className="text-slate-400 text-[10px]">{new Date(r.ts).toLocaleString()}</div>
              </div>
              <div className="font-black text-slate-800">{centsToUsd(r.amount_cents)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TicketsList({ token, pin, preview }) {
  const [data, setData] = useState([]);
  useEffect(() => {
    const params = new URLSearchParams();
    if (preview) params.set("preview", "1");
    if (pin) params.set("pin", pin);
    axios.get(`${BASE}/api/public/chew/portal/${token}/tickets?${params.toString()}`)
      .then(r => setData(r.data.tickets || []))
      .catch(() => {});
  }, [token, pin, preview]);
  if (data.length === 0) return null;
  return (
    <div className="mt-4 bg-white border-2 border-[#0f172a]">
      <div className="p-2 border-b-2 border-slate-200 font-black uppercase tracking-widest text-[10px]">Your tickets</div>
      <div className="divide-y divide-slate-200 max-h-[60vh] overflow-y-auto">
        {data.map(t => (
          <div key={t.id} className="p-2 text-xs" data-testid={`ticket-${t.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-black">{t.subject}</div>
              <div className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-100 border border-slate-300">{t.status}</div>
            </div>
            <div className="text-slate-400 text-[10px] mt-0.5">
              {new Date(t.created_at).toLocaleDateString()} · {t.category}
              {t.needs_approval && <span className="ml-1 text-amber-600">· Leslie CC'd</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ChewStudentPortal() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const [info, setInfo] = useState(null);
  const [pin, setPin] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [mode, setMode] = useState("home");
  const [triageSession, setTriageSession] = useState("");
  const [loading, setLoading] = useState(true);

  const loadInfo = () =>
    axios.get(`${BASE}/api/public/chew/portal/${token}?preview=${preview ? 1 : 0}`)
      .then(r => {
        setInfo(r.data);
        setFirstName(r.data.student_first_name || "");
      });

  useEffect(() => {
    loadInfo()
      .catch(() => toast.error("Link invalid"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, preview]);

  const handleUnlock = (p, meta) => {
    setPin(p);
    if (meta?.student_first_name) setFirstName(meta.student_first_name);
    // Refresh info so last4 / move-in appear in the header now that
    // we're properly authorized as this student.
    loadInfo().catch(() => {});
  };

  if (loading) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-400 text-xs">Loading…</div>;
  if (!info) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-500">Invalid link</div>;

  // YIQ contrast so any landlord color (yellow, navy, maroon) stays readable.
  const brand = info.brand_color || "#0f172a";
  const textOnBrand = (() => {
    const h = brand.replace("#", "");
    if (h.length < 6) return "#fff";
    const r = parseInt(h.slice(0, 2), 16),
          g = parseInt(h.slice(2, 4), 16),
          b = parseInt(h.slice(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 >= 165 ? "#0f172a" : "#fff";
  })();
  const accentColor = textOnBrand === "#0f172a" ? "#0f172a" : "#f59e0b";

  // Auth gate: first visit → identity form; returning → PIN. Preview bypasses.
  if (!preview && !pin) {
    if (!info.identity_set) {
      return (
        <IdentityScreen
          token={token}
          unitLabel={`Unit ${info.unit}`}
          brand={brand}
          textOnBrand={textOnBrand}
          knownResidents={info.known_residents || []}
          onDone={handleUnlock}/>
      );
    }
    return <PinScreen token={token} onDone={handleUnlock}/>;
  }

  const greetingName = firstName || info.student_first_name || "";

  return (
    <div className="min-h-screen bg-slate-100" data-testid="chew-student-portal">
      <header style={{ background: brand, color: textOnBrand }} data-testid="student-brand-header">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center gap-2">
            {info.brand_logo_url ? (
              <div className="bg-white rounded p-1 flex-shrink-0" data-testid="student-brand-logo-wrap">
                <img
                  src={info.brand_logo_url.startsWith("http") ? info.brand_logo_url : `${BASE}${info.brand_logo_url}`}
                  alt="Landlord logo"
                  data-testid="student-brand-logo"
                  className="w-8 h-8 rounded object-contain block"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}/>
              </div>
            ) : (
              <Shield weight="fill" size={22} style={{ color: accentColor }}/>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: accentColor }}>
                Chew Flats · Unit {info.unit} {preview && <span className="ml-1 px-1.5 py-0.5" style={{ background: accentColor, color: brand }}>PREVIEW</span>}
              </div>
              <div className="font-display font-black text-lg leading-tight truncate" data-testid="student-greeting">
                {greetingName ? `Hi ${greetingName} 👋` : `Unit ${info.unit} · ${info.landlord_name}`}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto p-4">
        {mode !== "home" && (
          <button onClick={() => setMode("home")} data-testid="portal-back"
            className="mb-3 text-xs font-black uppercase tracking-widest text-slate-600 hover:text-black inline-flex items-center gap-1">
            <ArrowLeft weight="bold" size={12}/> Back
          </button>
        )}
        {mode === "home" && (
          <>
            <HomeTiles info={info} onGo={setMode}/>
            <TicketsList token={token} pin={pin} preview={preview}/>
          </>
        )}
        {mode === "triage" && (
          <TriageChat token={token} pin={pin} preview={preview}
            onEscalate={(sid) => { setTriageSession(sid); setMode("ticket"); }}/>
        )}
        {mode === "ticket" && (
          <TicketForm token={token} pin={pin} preview={preview} info={info}
            initialSession={triageSession} onSubmitted={() => setMode("home")}/>
        )}
        {mode === "laundry" && <LaundryPanel token={token} pin={pin} preview={preview}/>}
        {mode === "codes" && <DoorCodesPanel token={token} unitLabel={`Unit ${info.unit}`}/>}
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 text-center mt-6">
          Powered by Field Radar · LaSalle Student Pilot
        </div>
      </div>
    </div>
  );
}
